<template>
  <div v-if="editor" class="blip-toolbar">
    <!-- 서식 컨트롤: 위지윅(에디터) 모드에서만 노출. HTML 모드는 원시 텍스트 편집이라 의미 없음. -->
    <template v-if="mode === 'wysiwyg'">
      <button type="button" :class="btn('bold')" title="굵게" @click="cmd().toggleBold().run()">
        <b>B</b>
      </button>
      <button type="button" :class="btn('italic')" title="기울임" @click="cmd().toggleItalic().run()">
        <i>I</i>
      </button>
      <button type="button" :class="btn('strike')" title="취소선" @click="cmd().toggleStrike().run()">
        <s>S</s>
      </button>
      <span class="blip-toolbar__sep" />
      <button
        type="button"
        :class="btn('heading', { level: 2 })"
        title="제목 2"
        @click="cmd().toggleHeading({ level: 2 }).run()"
      >
        H2
      </button>
      <button
        type="button"
        :class="btn('heading', { level: 3 })"
        title="제목 3"
        @click="cmd().toggleHeading({ level: 3 }).run()"
      >
        H3
      </button>
      <button
        type="button"
        :class="btn('bulletList')"
        title="글머리 목록"
        @click="cmd().toggleBulletList().run()"
      >
        •
      </button>
      <button
        type="button"
        :class="btn('orderedList')"
        title="번호 목록"
        @click="cmd().toggleOrderedList().run()"
      >
        1.
      </button>
      <button
        type="button"
        :class="btn('blockquote')"
        title="인용"
        @click="cmd().toggleBlockquote().run()"
      >
        &ldquo;
      </button>
      <template v-if="fontSize">
        <span class="blip-toolbar__sep" />
        <select
          class="blip-toolbar__select"
          title="글자 크기 프리셋"
          :value="currentFontSize()"
          @change="onFontSize"
        >
          <option v-for="p in fontSizePresets" :key="p.label" :value="p.value || ''">
            {{ p.label }}
          </option>
        </select>
        <input
          type="number"
          class="blip-toolbar__size"
          min="8"
          max="96"
          step="1"
          title="글자 크기(px) 직접 입력 · 비우면 기본"
          placeholder="px"
          :value="currentFontSizeNum()"
          @change="onFontSizeNum"
        />
        <span class="blip-toolbar__unit">px</span>
      </template>
      <span class="blip-toolbar__sep" />
      <button type="button" :class="btn('link')" title="링크" @click="toggleLink">
        🔗
      </button>
      <button
        v-if="image"
        type="button"
        class="blip-toolbar__btn"
        title="이미지"
        @click="pickImage"
      >
        🖼
      </button>
      <template v-if="tokens">
        <span class="blip-toolbar__sep" />
        <button
          v-for="t in fundingTokens"
          :key="t.key"
          type="button"
          class="blip-toolbar__btn blip-toolbar__token"
          :title="`토큰 삽입: ${t.label}`"
          @click="insertToken(t.key)"
        >
          {{ t.label }}
        </button>
      </template>
      <span class="blip-toolbar__sep" />
    </template>
    <!-- HTML 모드: 소스 들여쓰기 정렬(뷰티파이). 위지윅 모드에선 의미 없어 숨김. -->
    <button
      v-if="mode === 'html'"
      type="button"
      class="blip-toolbar__btn blip-toolbar__format"
      title="HTML 들여쓰기 정렬"
      @click="$emit('format-source')"
    >
      정렬
    </button>
    <!-- 편집 방식 선택: 에디터(위지윅) ↔ HTML(직접 편집 + 렌더 미리보기). 항상 노출. -->
    <div class="blip-toolbar__modes" role="group" aria-label="편집 방식">
      <button
        type="button"
        :class="modeBtn('wysiwyg')"
        title="에디터로 편집"
        @click="$emit('set-mode', 'wysiwyg')"
      >
        에디터
      </button>
      <button
        type="button"
        :class="modeBtn('html')"
        title="HTML로 편집"
        @click="$emit('set-mode', 'html')"
      >
        HTML
      </button>
    </div>
    <input
      ref="fileInput"
      type="file"
      accept="image/*"
      class="blip-toolbar__file"
      @change="onFile"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import type { Editor } from "@tiptap/vue-2";
import {
  FUNDING_TOKENS,
  BLIP_TOKEN_NAME,
  FONT_SIZE_NAME,
  FONT_SIZE_PRESETS,
} from "@space-oddity-inc/editor-core";

export default defineComponent({
  name: "BlipToolbar",
  props: {
    editor: { type: Object as PropType<Editor>, required: true },
    tokens: { type: Boolean, default: true },
    image: { type: Boolean, default: true },
    fontSize: { type: Boolean, default: true },
    /** 편집 방식: "wysiwyg"(에디터) | "html"(직접 편집). */
    mode: { type: String as PropType<"wysiwyg" | "html">, default: "wysiwyg" },
    uploadImage: {
      type: Function as PropType<(file: File) => Promise<string>>,
      default: undefined,
    },
  },
  data() {
    return {
      fundingTokens: FUNDING_TOKENS,
      fontSizePresets: FONT_SIZE_PRESETS,
      // 에디터는 markRaw(비반응) → 선택/내용 변화가 prop 참조를 바꾸지 않아 툴바가
      // 자동 리렌더되지 않는다. 트랜잭션마다 강제 리렌더해 isActive·현재 글자 크기
      // 표시를 현재 선택과 동기화한다(클릭 이동 포함).
      txHandler: null as (() => void) | null,
    };
  },
  mounted(): void {
    const handler = (): void => this.$forceUpdate();
    this.txHandler = handler;
    this.editor.on("transaction", handler);
  },
  beforeDestroy(): void {
    if (this.txHandler) this.editor.off("transaction", this.txHandler);
  },
  methods: {
    // 커맨드 타입은 익스텐션별 모듈 augmentation에서 오므로, vue2 레이어에서
    // 전부 가시화하지 않는다. 잘 정의된 tiptap 커맨드 호출 seam에서만 느슨하게 둔다.
    cmd(): any {
      return this.editor.chain().focus();
    },
    btn(name: string, attrs?: Record<string, unknown>): unknown[] {
      return ["blip-toolbar__btn", { "is-active": this.editor.isActive(name, attrs) }];
    },
    modeBtn(value: "wysiwyg" | "html"): unknown[] {
      return ["blip-toolbar__mode", { "is-active": this.mode === value }];
    },
    // 현재 선택의 글자 크기(없으면 ""=기본). 트랜잭션마다 툴바 재렌더 시 재계산.
    currentFontSize(): string {
      return (this.editor.getAttributes(FONT_SIZE_NAME).size as string) || "";
    },
    // 숫자 입력용: "18px" → "18", 없으면 "".
    currentFontSizeNum(): string {
      const px = parseInt(this.currentFontSize(), 10);
      return Number.isFinite(px) ? String(px) : "";
    },
    onFontSize(e: Event): void {
      const value = (e.target as HTMLSelectElement).value;
      if (value) this.cmd().setFontSize(value).run();
      else this.cmd().unsetFontSize().run();
    },
    onFontSizeNum(e: Event): void {
      const raw = (e.target as HTMLInputElement).value.trim();
      const px = parseInt(raw, 10);
      if (raw === "" || !Number.isFinite(px) || px <= 0) {
        this.cmd().unsetFontSize().run();
        return;
      }
      this.cmd().setFontSize(`${px}px`).run();
    },
    toggleLink(): void {
      if (this.editor.isActive("link")) {
        this.cmd().unsetLink().run();
        return;
      }
      const url = window.prompt("링크 URL을 입력하세요");
      if (url) this.cmd().setLink({ href: url }).run();
    },
    insertToken(key: string): void {
      this.cmd().insertContent({ type: BLIP_TOKEN_NAME, attrs: { key } }).run();
    },
    pickImage(): void {
      (this.$refs.fileInput as HTMLInputElement).click();
    },
    async onFile(e: Event): Promise<void> {
      const input = e.target as HTMLInputElement;
      const file = input.files && input.files[0];
      input.value = "";
      if (!file) return;
      if (!this.uploadImage) {
        window.alert("이미지 업로더(uploadImage)가 설정되지 않았습니다.");
        return;
      }
      const src = await this.uploadImage(file);
      if (src) this.cmd().setImage({ src }).run();
    },
  },
});
</script>

<style scoped>
.blip-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  padding: 6px;
  border: 1px solid #e5e7eb;
  border-bottom: 0;
  border-radius: 6px 6px 0 0;
  background: #fafafa;
}
.blip-toolbar__btn {
  min-width: 28px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: #374151;
  font-size: 13px;
  cursor: pointer;
}
.blip-toolbar__btn:hover {
  background: #eef2f7;
}
.blip-toolbar__btn.is-active {
  background: #e0e7ff;
  border-color: #c7d2fe;
  color: #3730a3;
}
.blip-toolbar__token {
  font-size: 12px;
  color: #3730a3;
}
.blip-toolbar__select {
  height: 28px;
  padding: 0 4px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  color: #374151;
  font-size: 12px;
  cursor: pointer;
}
.blip-toolbar__size {
  width: 52px;
  height: 28px;
  padding: 0 6px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  color: #374151;
  font-size: 12px;
}
.blip-toolbar__unit {
  margin-left: -1px;
  color: #9ca3af;
  font-size: 11px;
}
.blip-toolbar__format {
  margin-left: auto;
  color: #3730a3;
}
.blip-toolbar__modes {
  display: inline-flex;
  margin-left: auto;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}
.blip-toolbar__mode {
  height: 28px;
  padding: 0 10px;
  border: 0;
  background: #fff;
  color: #6b7280;
  font-size: 12px;
  cursor: pointer;
}
.blip-toolbar__mode + .blip-toolbar__mode {
  border-left: 1px solid #e5e7eb;
}
.blip-toolbar__mode:hover {
  background: #f3f4f6;
}
.blip-toolbar__mode.is-active {
  background: #3730a3;
  color: #fff;
}
.blip-toolbar__sep {
  width: 1px;
  height: 18px;
  margin: 0 4px;
  background: #e5e7eb;
}
.blip-toolbar__file {
  display: none;
}
</style>
